import { FC, useContext } from "react";

import {
  Analytics,
  AssessmentOutlined,
  Assignment,
  Description,
  PhoneInTalk,
  School,
  Settings,
} from "@mui/icons-material";
import {
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useSidebar } from "@/contexts/SidebarContext";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense?: boolean;
  logout?: () => void;
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const RubiReportV2Menu: FC<Props> = ({ dense = false, collapsed = false }) => {
  const { t } = useTranslation("training");
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgID = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";

  const { isDevEnv } = useDevEnvironment();
  const currentUserPermissions =
    currentUser?.permissions?.map((p) => p.title) ?? [];
  const permissions = userPermissionChecks(currentUserPermissions);
  const canConfigureReportV2 = permissions.canCreateSurveyRV2;

  const hideSideBar = () => {
    setCollapsed(true);
  };

  const menuItems = [
    {
      to: `/rubireportv2/${orgID}/reports`,
      text: "Reports",
      icon: <AssessmentOutlined />,
    },
    {
      to: `/rubireportv2/${orgID}/citizen-engage`,
      text: "CitizenENGAGE",
      icon: <PhoneInTalk />,
    },
    {
      to: `/rubireportv2/${orgID}/analytics`,
      text: "Dashboard",
      icon: <Analytics />,
    },
    {
      to: `/rubireportv2/${orgID}/settings`,
      text: "Settings",
      icon: <Settings />,
    },
    ...(isDevEnv || canConfigureReportV2
      ? [
          {
            to: `/rubireportv2/${orgID}/forms`,
            text: "Forms",
            icon: <Description />,
          },
        ]
      : []),
    {
      to: `/rubireportv2/${orgID}/training`,
      text: t("menuLabel"),
      icon: <School />,
    },
  ];

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>REPORT</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip title="Online Reporting System" placement="right" arrow>
            <IconButton
              aria-label="RubiREPORT App"
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <Assignment />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.to}
            title={collapsed ? item.text : ""}
            placement="right"
            arrow
          >
            <MenuItem
              component={NavLink}
              to={item.to}
              onClick={isSmall ? hideSideBar : undefined}
              sx={[
                frameNavItemSx(),
                {
                  pl: collapsed ? 1 : 2,
                  py: dense ? 1 : 1.5,
                  minHeight: collapsed ? 48 : "auto",
                  justifyContent: collapsed ? "center" : "flex-start",
                },
              ]}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </MenuItem>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default RubiReportV2Menu;
